import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../hooks/use-toast';
import { useAppSelector } from '../app/hooks';
import { selectIsAuthenticated } from '../features/auth/authSlice';
import { Creator20Modal } from '../components/discountPrograms/Creator20Modal';
import { Creator30Modal } from '../components/discountPrograms/Creator30Modal';
import { AffiliateReferralModal } from '../components/discountPrograms/AffiliateReferralModal';
import { AffiliateStatsModal } from '../components/discountPrograms/AffiliateStatsModal';
import { discountApi } from '../services/discountApi';
import { analytics } from '../services/analytics';

import { Info } from 'lucide-react';
import { BarChart3 } from 'lucide-react';

interface InfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  intro?: string;
  about?: string;
  benefitsTitle?: string;
  benefits?: string[];
  audienceBenefitsTitle?: string;
  audienceBenefits?: string[];
  howToJoinTitle?: string;
  howToJoin?: string;
  outro?: string;
}

const InfoModal = ({ 
  isOpen, 
  onClose, 
  title, 
  intro, 
  about, 
  benefitsTitle, 
  benefits, 
  audienceBenefitsTitle, 
  audienceBenefits,
  howToJoinTitle, 
  howToJoin, 
  outro 
}: InfoModalProps) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="relative w-full max-w-lg rounded-xl bg-background p-8 shadow-2xl border border-white/10 my-8">
        <button
          onClick={onClose}
          className="absolute right-6 top-6 text-muted-foreground hover:text-foreground transition-colors"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="space-y-6">
          <div className="flex items-center gap-3 border-b border-white/10 pb-4">
            <div className="rounded-full bg-primary/10 p-2 text-primary">
              <Info className="h-6 w-6" />
            </div>
            <h2 className="text-xl font-bold">{title}</h2>
          </div>

          <div className="space-y-4 py-2">
            {intro && <p className="font-semibold text-foreground italic">{intro}</p>}
            {about && <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{about}</p>}
            
            {benefits && benefits.length > 0 && (
              <div className="space-y-2">
                {benefitsTitle && <h3 className="font-bold text-sm uppercase tracking-wider">{benefitsTitle}</h3>}
                <div className="space-y-2">
                  {benefits.map((detail, index) => (
                    <div key={index} className="flex gap-3 items-start pl-2">
                      <div className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                      <p className="text-muted-foreground text-sm leading-relaxed">{detail}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {audienceBenefits && audienceBenefits.length > 0 && (
              <div className="space-y-2 mt-4">
                {audienceBenefitsTitle && <h3 className="font-bold text-sm uppercase tracking-wider">{audienceBenefitsTitle}</h3>}
                <div className="space-y-2">
                  {audienceBenefits.map((detail, index) => (
                    <div key={index} className="flex gap-3 items-start pl-2">
                      <div className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-500" />
                      <p className="text-muted-foreground text-sm leading-relaxed">{detail}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {howToJoin && (
              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                {howToJoinTitle && <h3 className="font-bold text-sm uppercase tracking-wider">{howToJoinTitle}</h3>}
                <p className="text-muted-foreground text-sm leading-relaxed whitespace-pre-line">{howToJoin}</p>
              </div>
            )}

            {outro && <p className="text-foreground font-medium text-sm pt-2">{outro}</p>}
          </div>

          <button
            onClick={onClose}
            className="w-full rounded-lg bg-foreground px-4 py-3 text-sm font-semibold text-background hover:bg-foreground/90 transition-all active:scale-[0.98]"
          >
            {t('discountPrograms.gotIt', 'Got it')}
          </button>
        </div>
      </div>
    </div>
  );
};

interface DiscountCardProps {
  title: string;
  discount: string | React.ReactNode;
  subtitle?: string;
  secondaryLabel?: React.ReactNode;
  description: string;
  cta?: {
    label: string;
    action: () => void;
    disabled?: boolean;
  };
  secondaryCta?: {
    label: string;
    action: () => void;
  };
  badge?: string;
  variant: 'creator20' | 'creator25' | 'creator30' | 'affiliate';
  bgImage: string;
  modalContent: {
    title: string;
    intro?: string;
    about?: string;
    benefitsTitle?: string;
    benefits?: string[];
    audienceBenefitsTitle?: string;
    audienceBenefits?: string[];
    howToJoinTitle?: string;
    howToJoin?: string;
    outro?: string;
  };
  isCurrentLevel?: boolean;
}

const DiscountCard = ({ title, discount, subtitle, secondaryLabel, description, cta, secondaryCta, badge, variant, bgImage, modalContent, isCurrentLevel }: DiscountCardProps) => {
  const { t } = useTranslation();
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const variantStyles = {
    creator20: 'border-blue-400/40 shadow-blue-500/15 bg-gradient-to-br from-blue-50/95 via-white/95 to-sky-50/90',
    creator25: 'border-emerald-400/40 shadow-emerald-500/15 bg-gradient-to-br from-emerald-50/95 via-white/95 to-lime-50/90',
    creator30: 'border-fuchsia-400/40 shadow-fuchsia-500/15 bg-gradient-to-br from-fuchsia-50/95 via-white/95 to-pink-50/90',
    affiliate: 'border-amber-400/40 shadow-amber-500/15 bg-gradient-to-br from-amber-50/95 via-white/95 to-orange-50/90',
  };

  return (
    <>
      <div className={`group relative flex h-full flex-col overflow-hidden rounded-xl border p-6 shadow-lg transition-all hover:scale-[1.02] hover:shadow-xl ${variantStyles[variant]} ${isCurrentLevel ? 'ring-2 ring-primary/40 border-primary/30' : ''}`}>
        {/* Background Image */}
        <div 
          className="pointer-events-none absolute inset-0 opacity-65 transition-opacity group-hover:opacity-80"
          style={{
            backgroundImage: `url(${bgImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        
        {/* Content Container (to ensure content is above background) */}
        <div className="relative z-10 flex-1 flex flex-col">
          <div className="flex-1 space-y-4 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="min-h-[140px] flex flex-col justify-start">
              <h3 className="min-w-0 truncate whitespace-nowrap text-xs font-bold uppercase tracking-widest text-slate-700">
                {title}
              </h3>
              <div className="mt-1 flex flex-col">
                <div className="flex items-baseline gap-2">
                  <div className="text-7xl font-black tracking-tighter leading-none text-slate-900">{discount}</div>
                  {subtitle && <span className="text-sm font-bold uppercase text-slate-700">{subtitle}</span>}
                </div>
                {badge ? (
                  <span className="mt-2 ml-auto inline-flex w-fit rounded-full bg-foreground px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-background shadow-sm">
                    {badge}
                  </span>
                ) : null}
                {secondaryLabel && (
                  <div className="mt-2 text-[10px] font-bold uppercase tracking-wider text-slate-700">
                    {secondaryLabel}
                  </div>
                )}
              </div>
            </div>
            <p className="text-sm font-medium leading-relaxed text-slate-800">{description}</p>
            {secondaryCta ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  secondaryCta.action();
                }}
                className="mx-auto inline-flex w-fit items-center gap-1.5 rounded-md border border-slate-400/50 bg-white/70 px-3 py-1.5 text-xs font-semibold text-slate-900 transition-colors hover:bg-white/90"
              >
                <BarChart3 className="h-3.5 w-3.5" />
                {secondaryCta.label}
              </button>
            ) : null}
            
            <div className="flex-1" /> {/* Spacer to push the link down */}
            
            <button 
              onClick={(e) => {
                e.stopPropagation();
                setIsInfoOpen(true);
              }}
              className="mt-4 flex items-center gap-1.5 text-xs font-semibold text-slate-800 transition-colors hover:text-slate-950"
            >
              <Info className="h-3.5 w-3.5" />
              {t('discountPrograms.learnMore', 'Learn more about the program')}
            </button>
          </div>
          {cta ? (
            <div className="mt-6" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  cta.action();
                }}
                disabled={cta.disabled}
                className="w-full rounded-lg bg-foreground px-4 py-2.5 text-sm font-bold text-background transition-all hover:bg-foreground/90 active:scale-[0.98] disabled:opacity-50 shadow-md"
              >
                {cta.label}
              </button>
            </div>
          ) : (
            /* Placeholder to maintain alignment with cards that have buttons */
            <div className="mt-6 h-[42px]" />
          )}
        </div>
      </div>

      <InfoModal 
        isOpen={isInfoOpen} 
        onClose={() => setIsInfoOpen(false)} 
        {...modalContent}
      />
    </>
  );
};

const DiscountsPage = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const [activeModal, setActiveModal] = useState<'20' | '30' | null>(null);
  const [, setIsSubmitting] = useState(false);
  const [showAffiliateModal, setShowAffiliateModal] = useState(false);
  const [showAffiliateStatsModal, setShowAffiliateStatsModal] = useState(false);
  const [currentDiscount, setCurrentDiscount] = useState<{ type: string; percent: number } | null>(null);

  console.log('DiscountsPage Render:', { isAuthenticated, activeModal, showAffiliateModal });

  const handleApplyClick = (level: '20' | '30') => {
    console.log('handleApplyClick triggered for level:', level);
    analytics.track('discount_apply_button_clicked', { discountLevel: level });
    
    console.log('Opening modal for level:', level);
    setActiveModal(level);
  };

  const handleGetReferralLink = () => {
    console.log('handleGetReferralLink triggered');
    analytics.track('discount_get_referral_link_button_clicked');
    
    console.log('Opening affiliate modal');
    setShowAffiliateModal(true);
  };

  useEffect(() => {
    let cancelled = false;
    const loadDiscount = async () => {
      if (!isAuthenticated) {
        setCurrentDiscount(null);
        return;
      }
      try {
        const discount = await discountApi.getMyDiscount();
        if (!cancelled) {
          if (discount?.type && typeof discount.percent === 'number') {
            setCurrentDiscount({ type: discount.type, percent: discount.percent });
          } else {
            setCurrentDiscount(null);
          }
        }
      } catch {
        if (!cancelled) {
          setCurrentDiscount(null);
        }
      }
    };
    loadDiscount();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const handleSubmit20 = async (data: {
    artworkFiles: string[];
    artworkLinks: string[];
    socialLinks: string[];
    communicationMethod: string;
    comment: string;
  }) => {
    setIsSubmitting(true);
    try {
      await discountApi.submitCreatorApplication({
        requestedLevel: 'CREATOR_20',
        ...data,
      });
      setActiveModal(null);
      toast({
        title: t('discountPrograms.applicationSubmitted', 'Application submitted successfully!'),
        variant: 'default',
      });
    } catch (error: any) {
      console.error('Failed to submit:', error);
      const errorMessage = error.response?.data?.message;
      if (errorMessage === 'At least one artwork example is required') {
        toast({
          title: t('discountPrograms.errors.artworkRequired', 'At least one artwork example (file or link) is required for Creator Starter.'),
          variant: 'destructive',
        });
      } else if (errorMessage === 'You already have a pending application for this level') {
        toast({
          title: t('discountPrograms.errors.pendingApplication', 'You already have a pending application for Creator Starter. We will notify you once it\'s reviewed.'),
          variant: 'destructive',
        });
      } else {
        toast({
          title: t('discountPrograms.applicationError', 'Failed to submit application. Please try again.'),
          variant: 'destructive',
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit30 = async (data: {
    websiteLinks: string[];
    communicationMethod: string;
    comment: string;
  }) => {
    setIsSubmitting(true);
    try {
      await discountApi.submitCreatorApplication({
        requestedLevel: 'CREATOR_30',
        ...data,
      });
      setActiveModal(null);
      toast({
        title: t('discountPrograms.applicationSubmitted', 'Application submitted successfully!'),
        variant: 'default',
      });
    } catch (error: any) {
      console.error('Failed to submit:', error);
      const errorMessage = error.response?.data?.message;
      if (errorMessage === 'At least one website or portfolio link is required') {
        toast({
          title: t('discountPrograms.errors.websiteLinkRequired', 'At least one website or portfolio link is required for Creator Elite.'),
          variant: 'destructive',
        });
      } else if (errorMessage === 'You already have a pending application for this level') {
        toast({
          title: t('discountPrograms.errors.pendingApplication', 'You already have a pending application for Creator Elite. We will notify you once it\'s reviewed.'),
          variant: 'destructive',
        });
      } else {
        toast({
          title: t('discountPrograms.applicationError', 'Failed to submit application. Please try again.'),
          variant: 'destructive',
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
      <header className="mb-12 space-y-4 text-center">
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
          {t('discountPrograms.title', 'Discount Programs')}
        </h1>
        <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
          {t('discountPrograms.subtitle', 'Save more with our creator and affiliate programs')}
        </p>
        {currentDiscount ? (
          <div className="mx-auto mt-3 inline-flex items-center rounded-md border border-green-300 bg-green-100 px-4 py-2 text-sm font-semibold text-green-900">
            {t(
              'discountPrograms.currentDiscountHeader',
              'Your current discount is {{percent}}%.',
              { percent: currentDiscount.percent },
            )}
          </div>
        ) : null}
      </header>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {/* Creator Starter - 20% */}
        <DiscountCard
          variant="creator20"
          bgImage="/watercolor_bg_1.jpg"
          title={t('discountPrograms.creator20.title', 'Creator Starter')}
          discount="20%"
          subtitle={t('discountPrograms.off', 'OFF')}
          description={t('discountPrograms.creator20.description')}
          modalContent={{
            title: t('discountPrograms.creator20.modal.title'),
            intro: t('discountPrograms.creator20.modal.intro'),
            about: t('discountPrograms.creator20.modal.about'),
            benefitsTitle: t('discountPrograms.creator20.modal.benefits_title'),
            benefits: t('discountPrograms.creator20.modal.benefits', { returnObjects: true }) as string[],
            howToJoinTitle: t('discountPrograms.creator20.modal.how_to_join_title'),
            howToJoin: t('discountPrograms.creator20.modal.how_to_join'),
            outro: t('discountPrograms.creator20.modal.outro'),
          }}
          cta={{
            label: t('discountPrograms.creator20.cta', 'Apply 20%'),
            action: () => handleApplyClick('20'),
          }}
          isCurrentLevel={currentDiscount?.type === 'CREATOR_20'}
        />

        {/* Auto Upgrade - 25% */}
        <DiscountCard
          variant="creator25"
          bgImage="/watercolor_bg_2.jpg"
          title={t('discountPrograms.creator25.title', 'Creator Pro')}
          discount="25%"
          subtitle={t('discountPrograms.off', 'OFF')}
          description={t('discountPrograms.creator25.description')}
          modalContent={{
            title: t('discountPrograms.creator25.modal.title'),
            intro: t('discountPrograms.creator25.modal.intro'),
            about: t('discountPrograms.creator25.modal.about'),
            benefitsTitle: t('discountPrograms.creator25.modal.benefits_title'),
            benefits: t('discountPrograms.creator25.modal.benefits', { returnObjects: true }) as string[],
            howToJoinTitle: t('discountPrograms.creator25.modal.how_to_join_title'),
            howToJoin: t('discountPrograms.creator25.modal.how_to_join'),
            outro: t('discountPrograms.creator25.modal.outro'),
          }}
          badge={t('discountPrograms.creator25.badge', 'Auto-upgrade')}
          isCurrentLevel={currentDiscount?.type === 'CREATOR_25'}
        />

        {/* Pro Creator - 30% */}
        <DiscountCard
          variant="creator30"
          bgImage="/watercolor_bg_3.jpg"
          title={t('discountPrograms.creator30.title', 'Creator Elite')}
          discount="30%"
          subtitle={t('discountPrograms.off', 'OFF')}
          description={t('discountPrograms.creator30.description')}
          modalContent={{
            title: t('discountPrograms.creator30.modal.title'),
            intro: t('discountPrograms.creator30.modal.intro'),
            about: t('discountPrograms.creator30.modal.about'),
            benefitsTitle: t('discountPrograms.creator30.modal.benefits_title'),
            benefits: t('discountPrograms.creator30.modal.benefits', { returnObjects: true }) as string[],
            howToJoinTitle: t('discountPrograms.creator30.modal.how_to_join_title'),
            howToJoin: t('discountPrograms.creator30.modal.how_to_join'),
            outro: t('discountPrograms.creator30.modal.outro'),
          }}
          cta={{
            label: t('discountPrograms.creator30.cta', 'Apply 30%'),
            action: () => handleApplyClick('30'),
          }}
          isCurrentLevel={currentDiscount?.type === 'CREATOR_30'}
        />

        {/* Affiliate Program */}
        <DiscountCard
          variant="affiliate"
          bgImage="/watercolor_bg_4.jpg"
          title={t('discountPrograms.affiliate.title', 'Affiliate Program')}
          discount={
            <span className="flex items-baseline whitespace-nowrap">
              <span className="text-4xl">10% + 30%</span>
              <span className="ml-1 text-xl">+ 10%</span>
            </span>
          }
          description={t('discountPrograms.affiliate.description')}
          modalContent={{
            title: t('discountPrograms.affiliate.modal.title'),
            intro: t('discountPrograms.affiliate.modal.intro'),
            about: t('discountPrograms.affiliate.modal.about'),
            benefitsTitle: t('discountPrograms.affiliate.modal.benefits_title'),
            benefits: t('discountPrograms.affiliate.modal.benefits', { returnObjects: true }) as string[],
            audienceBenefitsTitle: t('discountPrograms.affiliate.modal.audience_benefits_title'),
            audienceBenefits: t('discountPrograms.affiliate.modal.audience_benefits', { returnObjects: true }) as string[],
            howToJoinTitle: t('discountPrograms.affiliate.modal.how_to_join_title'),
            howToJoin: t('discountPrograms.affiliate.modal.how_to_join'),
            outro: t('discountPrograms.affiliate.modal.outro'),
          }}
          cta={{
            label: t('discountPrograms.affiliate.cta', 'Get Referral Link'),
            action: handleGetReferralLink,
          }}
          secondaryCta={{
            label: t('discountPrograms.affiliate.statsCta', 'View referral stats'),
            action: () => setShowAffiliateStatsModal(true),
          }}
          isCurrentLevel={currentDiscount?.type === 'REFERRAL_10'}
        />
      </div>

      {/* Info Section */}
      <div className="mt-12 rounded-lg border bg-muted/50 p-6">
        <h2 className="mb-4 text-lg font-semibold">
          {t('discountPrograms.howItWorks', 'How It Works')}
        </h2>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground">
              1
            </div>
            <h3 className="font-medium">{t('discountPrograms.step1.title', 'Choose Your Level')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('discountPrograms.step1.description', 'Start with 20% off as a Creator, or earn 25% automatically after 5 prints.')}
            </p>
          </div>
          <div className="space-y-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground">
              2
            </div>
            <h3 className="font-medium">{t('discountPrograms.step2.title', 'Apply or Grow')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('discountPrograms.step2.description', 'Submit your portfolio for higher tiers, or let the 25% unlock automatically.')}
            </p>
          </div>
          <div className="space-y-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground">
              3
            </div>
            <h3 className="font-medium">{t('discountPrograms.step3.title', 'Share & Earn')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('discountPrograms.step3.description', 'Join our affiliate program to earn 10% on every referral order.')}
            </p>
          </div>
        </div>
      </div>

      {/* Modals */}
      <Creator20Modal
        isOpen={activeModal === '20'}
        onClose={() => setActiveModal(null)}
        onSubmit={handleSubmit20}
      />

      <Creator30Modal
        isOpen={activeModal === '30'}
        onClose={() => setActiveModal(null)}
        onSubmit={handleSubmit30}
      />

      <AffiliateReferralModal
        isOpen={showAffiliateModal}
        onClose={() => setShowAffiliateModal(false)}
      />

      <AffiliateStatsModal
        isOpen={showAffiliateStatsModal}
        onClose={() => setShowAffiliateStatsModal(false)}
      />
    </div>
  );
};

export default DiscountsPage;
